import React from 'react';

import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../../../context/ProductBuilder/machine';
import { getCurrentVariant } from '../../../../../helpers/getCurrentVariant';
import {
  getCurtainRailHexColour,
  getRollerHexColour,
} from '../../../../../helpers/getHexColorCode';
import {
  getPatternImages,
  getRollerDayNightPatternImages,
} from '../../../../../helpers/getPatternImages';

export const FrontRollLightGuardRollerDayNight = React.forwardRef(
  (
    props: React.SVGProps<SVGSVGElement>,
    setProductImageRef: React.Ref<SVGSVGElement>,
  ) => {
    const [state] = useService(productBuilderMachine);
    const patternUrl = getPatternImages(state);
    const nightPatternUrl = getRollerDayNightPatternImages(state);
    const currentVariant = getCurrentVariant(state.context);

    const topPattern =
      typeof state.context.custom.dayFabricPosition === 'undefined' ||
      state.context.custom.dayFabricPosition === 'Top'
        ? patternUrl
        : nightPatternUrl;

    const bottomPattern =
      state.context.custom.dayFabricPosition === 'Bottom'
        ? patternUrl
        : nightPatternUrl;

    const currentProduct = currentVariant
      ? state.context.products.find(product =>
          product.variants.find(variant => variant.id === currentVariant.id),
        )
      : '';

    const bracketColourTag = currentProduct
      ? currentProduct.tags
          .find(tag => tag.includes('Bracket Colour'))
          ?.replace('Bracket Colour:', '')
      : '';

    const bracketColour =
      typeof bracketColourTag === 'string' && bracketColourTag !== ''
        ? bracketColourTag.trim()
        : 'Silver';

    const bracketColourHex = getRollerHexColour(bracketColour);

    const bottomRailColour = state.context.custom.bottomRailColour
      ? state.context.custom.bottomRailColour
      : 'Silver';

    const bottomRailColourHex = getRollerHexColour(bottomRailColour);

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        data-name="Layer 1"
        viewBox="0 0 554.2 409"
        {...props}
        preserveAspectRatio="none"
      >
        <defs>
          <pattern
            id="rbDayNightFrontRoll"
            patternUnits="userSpaceOnUse"
            width="100"
            height="100"
          >
            <image href={topPattern} x="0" y="0" width="100" height="100" />
          </pattern>
        </defs>
        <defs>
          <pattern
            id="rbDayNightNightPattern"
            patternUnits="userSpaceOnUse"
            width="100"
            height="100"
          >
            <image href={bottomPattern} x="0" y="0" width="100" height="100" />
          </pattern>
        </defs>
        <defs>
          <linearGradient
            id="prefix__linear-gradient"
            x1={10.23}
            y1={-3232.66}
            x2={544.23}
            y2={-3232.66}
            gradientTransform="matrix(1 0 0 -1 -3.23 -2974.11)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset={0.01} stopColor="#b7b8ba" />
            <stop offset={0.29} stopColor="#94989c" />
            <stop offset={0.65} stopColor="#bbbabe" />
            <stop offset={0.87} stopColor="#9a9ca0" />
            <stop offset={1} stopColor="#a3a5a8" />
            <stop offset={1} stopColor="#bfc2c6" />
          </linearGradient>
          <linearGradient
            id="prefix__linear-gradient-2"
            x1={277.23}
            y1={-3158.21}
            x2={277.23}
            y2={-3143.23}
            gradientTransform="matrix(1 0 0 -1 -3.23 -2974.11)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset={0.49} stopOpacity={0} />
            <stop offset={1} stopColor="#4d4d4d" stopOpacity={0.3} />
          </linearGradient>
          <linearGradient
            id="prefix__linear-gradient-3"
            x1={10.23}
            y1={-3139.36}
            x2={544.23}
            y2={-3139.36}
            xlinkHref="#prefix__linear-gradient"
          />
          <linearGradient
            id="prefix__linear-gradient-4"
            x1={-981.58}
            y1={-2584.82}
            x2={-981.58}
            y2={-2566.76}
            gradientTransform="matrix(1 0 0 -1 1255.58 -2562.19)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset={0.49} stopOpacity={0} />
            <stop offset={1} stopOpacity={0.3} />
          </linearGradient>
          <style>{`.cls-7{fill:${bracketColourHex}}`}</style>
        </defs>
        <path
          style={{
            fill: 'url(#rbDayNightNightPattern)',
          }}
          d="M7 254.6h534v7.9H7z"
        />
        <path
          style={{
            isolation: 'isolate',
          }}
          fill="url(#rbDayNightNightPattern)"
          d="M7 4.6h534.1v250H7z"
        />
        <path
          style={{
            isolation: 'isolate',
          }}
          opacity={0.45}
          fill="url(#prefix__linear-gradient-2)"
          d="M6.9 169.1h534.2v15H6.9z"
        />
        <path fill="url(#rbDayNightFrontRoll)" d="M7 161.3h534v7.9H7z" />
        <path fill="url(#rbDayNightFrontRoll)" d="M7 4.6h534.1v156.7H7z" />
        <path
          style={{
            fill: 'url(#prefix__linear-gradient-4)',
          }}
          d="M6.9 4.6h534.2v18.1H6.9z"
        />

        <path
          fill={bracketColourHex}
          d="M542.6 0h11.6v409h-11.6zM0 0h11.6v409H0z"
          data-name="Light guard"
        />
        <path
          fill={bracketColourHex}
          className="cls-7"
          d="M532.1 0v.8h13.3V9h-1.2v9.4H543v-6.6a1.79 1.79 0 0 0-1.8-1.8v22.9a1.79 1.79 0 0 0 1.8-1.8v-6.5h2.5v4.8h2a.68.68 0 0 0 .7-.7V0ZM0 0v28.7a.68.68 0 0 0 .7.7h2v-4.8h2.5v6.5A1.79 1.79 0 0 0 7 32.9V10a1.79 1.79 0 0 0-1.8 1.8v6.6H3.9V9H2.7V.8H16V0Z"
        />
      </svg>
    );
  },
);
